/**
 * SERVER DATA GRID — il blocco `<DataGrid>` server-side condiviso da tutte le
 * liste paginate del CRM (pipeline, kanban, quotation, business, rapidContact,
 * queue). Incapsula le props IDENTICHE ovunque (paginazione/sort server-side,
 * `pageSizeOptions`, zebra, `autoHeight`, ecc.) e lascia al chiamante solo ciò
 * che varia: righe, colonne, `getRowId`, i modelli di paginazione/sort e i loro
 * handler, `loading`, `rowCount`, e i pass-through `className`/`sx`/`getRowHeight`.
 *
 * NON incapsula toolbar, ricerca, effetti di fetch, meccanismo di delete o
 * bottoni: quelle parti divergono legittimamente tra le liste e restano nei
 * singoli componenti (vedi code-review-general.md per le incoerenze da
 * normalizzare — stili inline, `useListDeleteHandler` non ovunque).
 */

import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridPaginationModel,
  type GridRowClassNameParams,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';

const zebra = (params: GridRowClassNameParams) =>
  params.indexRelativeToCurrentPage % 2 === 0 ? '' : 'even-row';

interface ServerDataGridProps<Row extends GridValidRowModel> {
  rows: Row[];
  columns: GridColDef[];
  getRowId: (row: Row) => number | string;
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  loading: boolean;
  className?: string;
  sx?: DataGridProps['sx'];
  getRowHeight?: DataGridProps['getRowHeight'];
  /**
   * Classe AGGIUNTIVA per riga, composta con la zebra invece di sostituirla.
   *
   * Serve a chi deve marcare una riga per quello che CONTIENE e non per dove sta: una pratica
   * archiviata, un record scaduto. Restituire la classe da qui e non da `renderCell` di una colonna
   * e' l'unico modo di colorare la RIGA intera — una cella non puo' raggiungere le sorelle.
   */
  getRowClassName?: (params: GridRowClassNameParams) => string;
}

export function ServerDataGrid<Row extends GridValidRowModel>({
  rows,
  columns,
  getRowId,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  loading,
  className,
  sx,
  getRowHeight,
  getRowClassName,
}: ServerDataGridProps<Row>) {
  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      autoHeight
      disableRowSelectionOnClick
      paginationMode="server"
      sortingMode="server"
      rowCount={rowCount}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50, 100]}
      loading={loading}
      getRowClassName={(params) =>
        [zebra(params), getRowClassName?.(params) ?? ''].filter(Boolean).join(' ')
      }
      getRowHeight={getRowHeight}
      className={className}
      sx={sx}
    />
  );
}
